import React, { useState } from 'react';

import {
  Logout as LogoutIcon,
  Person as PersonIcon,
  TourOutlined,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Divider,
  Menu,
  MenuItem,
  ItemIcon,
  Tooltip,
  Typography,
} from 'components';

import {
  ContentLoader,
  useNavigate,
  useParams,
  useSelector,
  useTour,
  useTranslation,
} from 'third-party';

import { loginUrl, userProfileUrl } from 'constants/url';

import { useApp } from 'modules/app';
import { useUser } from 'modules/user';

import { appSelectors } from 'selectors';

import { getAvatarUrl } from 'utils/app';
import { deleteUserToken } from 'utils/auth';

import { APIUserSettingType } from 'types/swaggerApi';

export const UserMenu = () => {
  const user = useSelector(appSelectors.user);
  const isNavigationCollapsed = useSelector(appSelectors.isNavigationCollapsed);
  const navigate = useNavigate();
  const { tenantId } = useParams();

  const { userLogOut } = useApp();
  const { updateUserSettings } = useUser();
  const { setIsOpen, setCurrentStep } = useTour();
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onLogOutClick = async () => {
    await userLogOut();
    deleteUserToken();
    document.cookie.split(';').forEach(c => {
      document.cookie = c
        .replace(/^ +/, '')
        .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
    });
    navigate(loginUrl());
  };

  const onProfileClick = () => {
    navigate(userProfileUrl(tenantId));
  };

  const handleUserTourReset = () => {
    updateUserSettings({ type: APIUserSettingType.UserTour, value: 'reset' });
    navigate('/');
    setCurrentStep(0);
    setIsOpen(true);
  };

  // Initials for avatar fallback
  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : '';

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '20px 24px',
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease',
          overflow: 'hidden',
          '&:hover': {
            bgcolor: 'background.default',
          },
        }}
      >
        {!user ? (
          <ContentLoader
            speed={2}
            width={32}
            height={32}
            viewBox="0 0 32 32"
            backgroundColor="#F3F4F6"
            foregroundColor="#E5E7EB"
          >
            <circle cx="16" cy="16" r="16" />
          </ContentLoader>
        ) : (
          <Tooltip title="Account settings" arrow placement="top">
            <Avatar
              src={getAvatarUrl(user)}
              sx={{
                width: '32px',
                height: '32px',
                fontSize: '12px',
                fontWeight: 600,
                bgcolor: 'grey.100',
                color: 'text.primary',
                flexShrink: 0,
                fontFamily: 'Montserrat, sans-serif',
                textTransform: 'uppercase',
              }}
            >
              {initials}
            </Avatar>
          </Tooltip>
        )}

        {!isNavigationCollapsed && (
          <>
            {!user ? (
              <ContentLoader
                speed={2}
                width={120}
                height={20}
                viewBox="0 0 120 20"
                backgroundColor="#F3F4F6"
                foregroundColor="#E5E7EB"
              >
                <rect x="0" y="2" rx="4" ry="4" width="120" height="16" />
              </ContentLoader>
            ) : (
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'text.primary',
                  fontFamily: 'Montserrat, sans-serif',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.4,
                }}
              >
                {user.firstName} {user.lastName}
              </Typography>
            )}
          </>
        )}
      </Box>

      {user && (
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 4px 16px rgba(0,0,0,0.12))',
                mb: 1.5,
                minWidth: '180px',
                '&::before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  bottom: 0,
                  left: 20,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(50%) rotate(45deg)',
                  zIndex: 0,
                  borderRight: '1px solid',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
              },
            },
          }}
          transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        >
          <MenuItem onClick={() => { handleClose(); onProfileClick(); }}>
            <ItemIcon icon={PersonIcon} />
            {t('menu.profile')}
          </MenuItem>
          <MenuItem onClick={() => { handleClose(); handleUserTourReset(); }}>
            <ItemIcon icon={TourOutlined} />
            {t('menu.tour')}
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { handleClose(); onLogOutClick(); }}>
            <ItemIcon icon={LogoutIcon} />
            {t('menu.logOut')}
          </MenuItem>
        </Menu>
      )}
    </>
  );
};
